import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AppSidebar from './AppSidebar';
import { Navbar } from './common/Catalyst/navbar';
import { SidebarLayout } from './common/Catalyst/sidebar-layout';
import { EllipsisHorizontalIcon } from '@heroicons/react/16/solid';
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from './common/Catalyst/dropdown';
import { ErrorAlert } from './common/Custom/ErrorAlert';
import { FilterBar } from './violation/linear/FilterBar';
import {
  DirectoryColumn,
  DirectoryList,
  StatusCell,
} from './shell/DirectoryList';
import { UserReturn } from './clients/rust-api-client';
import { userService } from './clients/ApiClients';
import { getUserTypeFromToken } from './utils/TokenUtils';
import {
  canCorrectUserEmail,
  canResetMfa,
} from './constants/UserTypes';
import LoadingSpinner from './LoadingSpinner';
import ShowAlert from './ShowAlert';
import { apiErrorMessage } from './utils/apiErrors';
import { useSyncedUsers } from './sync/hooks';
import { withOwnRows } from './common/Custom/Kanban/withOwnRows';
import CorrectEmailDialog from './user/CorrectEmailDialog';
import ResetMfaDialog from './user/ResetMfaDialog';

interface UserManagementPageProps {
  currentPage: string;
}

/** `UserType`'s values, as people say them. */
const ROLE_OPTIONS = [
  { value: 'super_admin', label: 'Super admin' },
  { value: 'owner', label: 'Owner' },
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'board_member', label: 'Board member' },
];

const roleLabel = (userType: string) =>
  ROLE_OPTIONS.find((r) => r.value === userType)?.label ?? userType;

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'deactivated', label: 'Deactivated' },
];

const userStatus = (u: UserReturn) =>
  u.is_active === false ? 'deactivated' : 'active';

const COLUMNS: DirectoryColumn<UserReturn>[] = [
  {
    key: 'email',
    label: 'Email',
    className: 'w-64 hidden md:block',
    render: (u) => u.email,
    sortValue: (u) => u.email,
  },
  {
    key: 'role',
    label: 'Role',
    className: 'w-32 hidden sm:block',
    render: (u) => roleLabel(u.user_type),
    sortValue: (u) => roleLabel(u.user_type),
  },
  {
    key: 'status',
    label: 'Sign-in',
    className: 'w-28',
    render: (u) => (
      <StatusCell
        active={u.is_active !== false}
        label={u.is_active === false ? 'Deactivated' : 'Active'}
      />
    ),
    sortValue: userStatus,
  },
];

/**
 * The user-management screen, which exists to carry two operations that
 * the WorkOS migration left without a home.
 *
 * **Correcting a colleague's email.** `update_user` refuses to write
 * `users.email` for any row with a `workos_user_id`, because writing it
 * directly leaves WorkOS holding the old address and the next sign-in
 * matches no row. The supported route is the user changing it themselves
 * — which is unreachable for the case that needs it most, an address
 * typed in wrongly, since the wrong address is the one they would have to
 * sign in with.
 *
 * **Resetting MFA.** `DELETE /user/{id}/mfa-factors` worked from a
 * terminal and nowhere else. The first person to lose a phone needed
 * someone with curl.
 *
 * **The two do not get the same permission, and that is the point of
 * splitting them rather than shipping one "manage users" right.**
 * Correcting an address does not lower the bar to an account — the new
 * address lands at WorkOS unverified and still has to be proved. Clearing
 * a second factor *does* lower it, and is what an attacker holding a
 * compromised Owner session would reach for. So email correction is
 * delegated to Owners and Admins; MFA reset stays SuperAdmin-only.
 *
 * Every one of those checks is enforced by the backend against the row.
 * What this page decides is only which controls are worth rendering.
 */
const UserManagementPage: React.FC<UserManagementPageProps> = ({
  currentPage,
}) => {
  const userType = useMemo(() => getUserTypeFromToken(), []);
  const mayCorrectEmail = canCorrectUserEmail(userType);
  const mayResetMfa = canResetMfa(userType);

  const [fetchedUsers, setUsers] = useState<UserReturn[]>([]);
  // The sync engine's copy when there is one (#561). It holds every
  // account `GET /user` would list for an Owner, Admin or SuperAdmin, and
  // only the viewer's own for anyone else — who get the endpoint's 403
  // rather than a list of one.
  const listsUsers = mayCorrectEmail || mayResetMfa;
  const syncedUsers = useSyncedUsers({});
  const fromStore = listsUsers && syncedUsers !== null;
  const [own, setOwn] = useState<ReadonlyMap<string, UserReturn>>(new Map());
  const users = useMemo(
    () =>
      fromStore && syncedUsers
        ? // The endpoint's order.
          [...withOwnRows(syncedUsers, own)].sort((a, b) =>
            a.email.localeCompare(b.email)
          )
        : fetchedUsers,
    [fromStore, syncedUsers, own, fetchedUsers]
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>('');
  const [roles, setRoles] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);

  const [correcting, setCorrecting] = useState<UserReturn | null>(null);
  const [resetting, setResetting] = useState<UserReturn | null>(null);

  const [alert, setAlert] = useState<{
    type: 'success' | 'error';
    title: string;
    message: string;
  } | null>(null);

  const load = useCallback(async () => {
    if (fromStore) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // No company_id: an Owner or Admin is scoped to their own by the
      // backend, from their row. Sending one would be ignored anyway.
      const { data } = await userService.listUsers({});
      setUsers(data);
    } catch (err) {
      setAlert({
        type: 'error',
        title: "Couldn't load users",
        message: apiErrorMessage(err, "Couldn't load users."),
      });
    } finally {
      setLoading(false);
    }
  }, [fromStore]);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return users.filter(
      (u) =>
        (!roles.length || roles.includes(u.user_type)) &&
        (!statuses.length || statuses.includes(userStatus(u))) &&
        (!needle ||
          u.email.toLowerCase().includes(needle) ||
          u.full_name.toLowerCase().includes(needle))
    );
  }, [users, search, roles, statuses]);

  const onCorrected = (updated: UserReturn) => {
    setUsers((current) =>
      current.map((u) => (u.id === updated.id ? updated : u))
    );
    setOwn((prev) => new Map(prev).set(updated.id, updated));
    setCorrecting(null);
    setAlert({
      type: 'success',
      title: 'Address updated',
      message:
        `${updated.email} is now the address on this account. They will ` +
        'be asked to verify it the next time they sign in, and their ' +
        'existing sessions have been ended.',
    });
  };

  const onMfaReset = (who: UserReturn) => {
    setResetting(null);
    setAlert({
      type: 'success',
      title: 'Multi-factor authentication reset',
      message: `${who.full_name} will be asked to enrol a new authenticator the next time they sign in.`,
    });
  };

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage={currentPage} />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
      // The list uses the whole panel and scrolls inside itself, with its
      // filter bar as the top row, like HOAs and vendors.
      fill
      flush
    >
      {/* No page heading: the sidebar already says where you are. A user
          has no page of their own, so rows do not open; what can be done
          to one lives in the row's "…" menu. */}
      <div className="flex min-h-0 flex-1 flex-col">
        {/* Flush against the panel's edges, alerts need their own inset. */}
        <div className="px-3 pt-3 empty:hidden">
          {alert?.type === 'error' && (
            <ErrorAlert title={alert.title} onRetry={load} onDismiss={() => setAlert(null)}>
              {alert.message}
            </ErrorAlert>
          )}
          {alert?.type === 'success' && (
            <ShowAlert
              alertType="success"
              alertTitle={alert.title}
              alertMessage={alert.message}
              isVisible={true}
              onClose={() => setAlert(null)}
              isInline={true}
              timeout={8000}
            />
          )}
        </div>

        <FilterBar
          fields={[
            { field: 'Role', options: ROLE_OPTIONS, values: roles, onChange: setRoles },
            { field: 'Sign-in', options: STATUS_OPTIONS, values: statuses, onChange: setStatuses },
          ]}
          search={search}
          onSearch={setSearch}
          searchLabel="Search users by name or email"
          onClearAll={() => {
            setRoles([]);
            setStatuses([]);
          }}
          shown={visible.length}
          total={users.length}
        />

        {loading ? (
          <LoadingSpinner />
        ) : (
          <DirectoryList
            items={visible}
            getKey={(u) => u.id}
            getName={(u) => u.full_name}
            isMuted={(u) => u.is_active === false}
            columns={COLUMNS}
            people
            actions={
              mayCorrectEmail || mayResetMfa
                ? (u) => (
                    <Dropdown>
                      <DropdownButton plain aria-label={`Actions for ${u.full_name}`}>
                        <EllipsisHorizontalIcon className="h-4 w-4" />
                      </DropdownButton>
                      <DropdownMenu anchor="bottom end">
                        {mayCorrectEmail && (
                          <DropdownItem onClick={() => setCorrecting(u)}>
                            Correct email…
                          </DropdownItem>
                        )}
                        {mayResetMfa && (
                          <DropdownItem onClick={() => setResetting(u)}>
                            Reset MFA…
                          </DropdownItem>
                        )}
                      </DropdownMenu>
                    </Dropdown>
                  )
                : undefined
            }
            emptyMessage={
              users.length === 0 ? 'No users.' : 'No users match these filters.'
            }
          />
        )}
      </div>

      {correcting && (
        <CorrectEmailDialog
          user={correcting}
          onClose={() => setCorrecting(null)}
          onCorrected={onCorrected}
        />
      )}

      {resetting && (
        <ResetMfaDialog
          user={resetting}
          onClose={() => setResetting(null)}
          onReset={() => onMfaReset(resetting)}
        />
      )}
    </SidebarLayout>
  );
};

export default UserManagementPage;
